import { BuildingDesign } from '@shared/models/design/building-design.model';
import {
  DesignExpressionVariables,
  type ActivityExpressionVariablesRecord,
} from '@shared/models/design/design-expression-variables.model';
import { getActivityExpressionVariablesRecordFromDesign } from './activity-expression-variables.helpers';
import { getStoreysExpressionVariablesByBuilding } from './storey.expression-variables.helpers';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { enumToRecord } from '@shared/helpers/object.helpers';
import { cacheFactory } from '@shared/helpers/function.helpers';
import { getDesignExpressionFunctions } from './design-expression-functions.helpers';
import {
  getActivityMixFromDesign,
  getLargestProgramActivityTypeFromActivityMix,
} from '../activity-mix.helpers';
import { getBuildingDisplayName } from '../building.helpers';
import {
  combineStoreySummaryVariables,
  getStackGfa,
  getStoreySumFromDesignExpressionVariables,
  toGetStoreySummary,
  withPartyWallGwa,
} from './storey-sum-from-design.helpers';
import type { StoreySummaryVariables } from '@shared/models/design/design-expression-variables.model';
import { logStoreyExpressionDebug } from './storey-expression-debug.helpers';

const ACTIVITY_TYPE_RECORD = enumToRecord(ActivityType);

/**
 * Site-wide storey summary: each building is summed on its own stack, then
 * combined so a sibling footprint is not treated as extra floors.
 * @param design {@link BuildingDesign} to summarize
 * @param programs Activity mix used by the CALCULATIONS storey sum. Omitted
 * when tests call this helper with only a design.
 */
export const summarizeDesignStoreyVariables = (
  design: BuildingDesign,
  programs: ActivityExpressionVariablesRecord = getActivityExpressionVariablesRecordFromDesign(
    design,
  ),
): StoreySummaryVariables => {
  const stacks = getStoreysExpressionVariablesByBuilding(design);
  const siteGfa = stacks.reduce(
    (acc, storeys) => acc + getStackGfa(storeys),
    0,
  );
  const summaries = stacks.map((storeys) =>
    withPartyWallGwa(
      toGetStoreySummary(
        getStoreySumFromDesignExpressionVariables({ storeys, programs }, [], {
          siteGfa,
        }),
      ),
      storeys,
    ),
  );
  return combineStoreySummaryVariables(summaries);
};

/**
 * Use design to calculate expression variables. TODO: Add more testing
 * @param design
 * @returns The design expression variables (cached)
 */
export const getDesignExpressionVariables = <
  T extends BuildingDesign,
  R = T extends BuildingDesign ? DesignExpressionVariables : undefined, // TODO: Can be removed when design is not optional
>(
  design?: BuildingDesign,
): R => {
  if (!design) return undefined as R;

  // Only run this once when the design is changed
  return cacheFactory(
    () => {
      /**
       * TODO, HERE the migration issues should be fixed
       *
       */

      // 1. Get variables tied to certain activities
      const activityExpressionVariablesRecord =
        getActivityExpressionVariablesRecordFromDesign(design);

      const activityMix = getActivityMixFromDesign(design);

      // `storeys` stays the first building so `storeys[n]` keeps a single stack.
      // `getStorey(n)` sums floor n across buildings; `storeys_count` is max floors.
      const storeysByBuilding = getStoreysExpressionVariablesByBuilding(design);
      const storeyVariables = storeysByBuilding[0] ?? [];

      // Design totals include every building; height is the tallest stack.
      const summaryVariables = summarizeDesignStoreyVariables(
        design,
        activityExpressionVariablesRecord,
      );
      const buildings = design.buildings.map((building, index) => ({
        id: building.id,
        name: getBuildingDisplayName(building, index),
        storeys: storeysByBuilding[index] ?? [],
      }));
      const base: DesignExpressionVariables = {
        ...summaryVariables,
        storeys: storeyVariables,
        storeysByBuilding,
        programs: activityExpressionVariablesRecord,
        ActivityType: ACTIVITY_TYPE_RECORD,
        location: design.location,
        ...getDesignExpressionFunctions({
          programs: activityExpressionVariablesRecord,
          buildings,
          siteSummary: summaryVariables,
          largestActivityType:
            getLargestProgramActivityTypeFromActivityMix(activityMix),
        }),
        // TODO: Fix this before remove old project variables (used)
        // ...getDesignProductVariables(products),
      };

      logStoreyExpressionDebug(base);
      return base as R;
    },
    'getDesignExpressionVariables',
    [design],
  );
};
